<template>
  <div>
    <!-- En-tête de page avec accessibilité et guidance -->
    <div
      class="page-header mb-4"
      aria-label="En-tête de la gestion des livreurs"
    >
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1
            class="page-title mb-0"
            aria-label="Gestion professionnelle des livreurs"
          >
            <feather-icon
              icon="UsersIcon"
              size="24"
              class="mr-2"
              aria-hidden="true"
            />
            Gestion des livreurs
          </h1>
          <p
            id="page-desc"
            class="text-muted mb-0"
          >
            Consultez, filtrez et gérez les livreurs de votre flotte logistique. Utilisez les filtres avancés pour affiner la recherche et surveillez les indicateurs clés de performance.
          </p>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            v-b-tooltip.hover
            title="Actualiser les données de la flotte logistique"
            variant="outline-primary"
            class="mr-2"
            :disabled="isLoading"
            aria-label="Actualiser la liste des livreurs"
            @click="refreshData"
          >
            <feather-icon
              :icon="isLoading ? 'RefreshCwIcon' : 'RefreshCwIcon'"
              size="16"
              :class="{ 'rotate-animation': isLoading }"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-if="user && user.role === 'admin'"
            :to="{ name: 'delivery-man-create' }"
            variant="primary"
            class="btn-gradient"
            aria-label="Ajouter un nouveau livreur à la flotte logistique"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              aria-hidden="true"
            />
            <span class="ml-1">Nouveau livreur</span>
          </b-button>
        </div>
      </div>
      <div
        class="sr-only"
        aria-live="polite"
      >
        Utilisez les filtres pour rechercher un livreur par nom, dépôt ou statut. Les actions sont accessibles via les boutons à droite de chaque ligne du tableau.
      </div>
    </div>

    <!-- Cartes de statistiques améliorées pour la flotte -->
    <b-row
      class="mb-4"
      aria-label="Statistiques de la flotte de livreurs"
    >
      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card
          class="kpi-card kpi-neutral h-100"
          aria-label="Nombre total de livreurs"
        >
          <div class="kpi-content">
            <div class="kpi-text">
              <div class="kpi-label">
                Total Livreurs
              </div>
              <div class="kpi-value text-dark">
                {{ deliveryManStats.total }}
              </div>
              <div class="kpi-subtitle">
                Équipe complète
              </div>
            </div>
            <div class="kpi-icon kpi-icon-neutral">
              <feather-icon
                icon="UsersIcon"
                size="18"
                class="text-secondary"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="kpi-card kpi-success h-100">
          <div class="kpi-content">
            <div class="kpi-text">
              <div class="kpi-label">
                Actifs
              </div>
              <div class="kpi-value text-success">
                {{ deliveryManStats.active }}
              </div>
              <div class="kpi-subtitle">
                {{ getPercentage(deliveryManStats.active, deliveryManStats.total) }}% disponibles
              </div>
            </div>
            <div class="kpi-icon kpi-icon-success">
              <feather-icon
                icon="UserCheckIcon"
                size="18"
                class="text-success"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="kpi-card kpi-danger h-100">
          <div class="kpi-content">
            <div class="kpi-text">
              <div class="kpi-label">
                Inactifs
              </div>
              <div class="kpi-value text-primary">
                {{ deliveryManStats.inactive }}
              </div>
              <div class="kpi-subtitle">
                {{ getPercentage(deliveryManStats.inactive, deliveryManStats.total) }}% indisponibles
              </div>
            </div>
            <div class="kpi-icon kpi-icon-danger">
              <feather-icon
                icon="UserXIcon"
                size="18"
                class="text-primary"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="3"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="kpi-card kpi-info h-100">
          <div class="kpi-content">
            <div class="kpi-text">
              <div class="kpi-label">
                Avec Véhicule
              </div>
              <div class="kpi-value text-info">
                {{ deliveryManStats.withCars }}
              </div>
              <div class="kpi-subtitle">
                {{ getPercentage(deliveryManStats.withCars, deliveryManStats.total) }}% équipés
              </div>
            </div>
            <div class="kpi-icon kpi-icon-info">
              <feather-icon
                icon="TruckIcon"
                size="18"
                class="text-info"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Enhanced Search and filter bar -->
    <b-card class="mb-4 filter-card">
      <div class="filter-header mb-3">
        <h5 class="mb-0">
          <feather-icon
            icon="FilterIcon"
            size="18"
            class="mr-2"
          />
          Filtres et Recherche
        </h5>
        <small class="text-muted">
          Trouvez rapidement vos livreurs
        </small>
      </div>

      <b-row>
        <!-- Search Field -->
        <b-col
          md="4"
          sm="6"
          class="mb-3"
        >
          <b-form-group label-class="form-label-premium" label="Recherche Globale">
            <b-input-group class="search-input-group">
              <b-form-input
                v-model="searchTerm"
                placeholder="Nom, email, téléphone..."
                class="premium-input enhanced-input"
                @input="onSearchChange"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  class="search-btn"
                  @click="getDeliveryMans"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <!-- Repository Filter: Only show for admin -->
        <b-col
          v-if="user && user.role === 'admin'"
          md="4"
          sm="6"
          class="mb-3"
        >
          <b-form-group label-class="form-label-premium" label="Filtrer par Dépôt">
            <v-select
              v-model="repositoryFilter"
              :options="repositories"
              label="name"
              placeholder="Tous les dépôts"
              :reduce="repo => repo.id"
              clearable
              class="enhanced-select"
              @input="getDeliveryMans"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="HomeIcon"
                    size="14"
                    class="mr-2 text-muted"
                  />
                  {{ option.name }}
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Status Filter -->
        <b-col
          md="4"
          sm="6"
          class="mb-3"
        >
          <b-form-group label-class="form-label-premium" label="Filtrer par Statut">
            <v-select
              v-model="statusFilter"
              :options="statusOptions"
              label="text"
              placeholder="Tous les statuts"
              :reduce="status => status.value"
              clearable
              class="enhanced-select"
              @input="getDeliveryMans"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <b-badge
                    :variant="option.variant"
                    pill
                    class="mr-2"
                  >
                    {{ option.text }}
                  </b-badge>
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Action Buttons Row -->
      <b-row class="mt-3">
        <b-col class="d-flex justify-content-between align-items-center flex-wrap">
          <!-- Left Actions -->
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <b-button-group class="mr-3">
              <b-button
                v-b-tooltip.hover
                title="Réinitialiser tous les filtres"
                variant="outline-secondary"
                @click="resetFilters"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="16"
                />
                <span class="d-none d-lg-inline ml-1">Reset</span>
              </b-button>

              <b-button
                v-b-tooltip.hover
                :title="`Basculer vers la vue ${viewMode === 'table' ? 'carte' : 'tableau'}`"
                :variant="viewMode === 'table' ? 'outline-secondary' : 'outline-primary'"
                @click="toggleViewMode"
              >
                <feather-icon
                  :icon="viewMode === 'table' ? 'GridIcon' : 'ListIcon'"
                  size="16"
                />
                <span class="d-none d-lg-inline ml-1">
                  {{ viewMode === 'table' ? 'Carte' : 'Liste' }}
                </span>
              </b-button>
            </b-button-group>

            <!-- Results Counter -->
            <small class="text-muted">
              {{ sortedDeliveryMans.length }} résultat(s) trouvé(s)
              <span v-if="hasActiveFilters">(filtré)</span>
            </small>
          </div>

          <!-- Right Actions -->
          <div class="d-flex align-items-center">
            <b-button
              v-b-tooltip.hover
              title="Exporter la liste actuelle en CSV"
              variant="outline-success"
              class="mr-2"
              :disabled="!sortedDeliveryMans.length"
              @click="exportToCSV"
            >
              <feather-icon
                icon="DownloadIcon"
                size="16"
              />
              <span class="d-none d-md-inline ml-1">Export CSV</span>
            </b-button>

            <b-button
              v-if="user && user.role === 'admin'"
              :to="{ name: 'delivery-man-create' }"
              variant="primary"
              class="btn-gradient"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
              />
              <span class="ml-1">Ajouter</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Active Filters Display -->
      <div
        v-if="hasActiveFilters"
        class="active-filters mt-3 pt-3 border-top"
      >
        <div class="d-flex align-items-center flex-wrap">
          <small class="text-muted mr-2 mb-1">
            <feather-icon
              icon="FilterIcon"
              size="12"
              class="mr-1"
            />
            Filtres actifs:
          </small>

          <b-badge
            v-if="searchTerm"
            variant="light-primary"
            class="filter-badge mr-2 mb-1"
          >
            <feather-icon
              icon="SearchIcon"
              size="12"
              class="mr-1"
            />
            "{{ searchTerm }}"
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-1 cursor-pointer"
              @click="searchTerm = ''; getDeliveryMans()"
            />
          </b-badge>

          <b-badge
            v-if="repositoryFilter"
            variant="light-info"
            class="filter-badge mr-2 mb-1"
          >
            <feather-icon
              icon="HomeIcon"
              size="12"
              class="mr-1"
            />
            {{ getRepositoryName(repositoryFilter) }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-1 cursor-pointer"
              @click="repositoryFilter = null; getDeliveryMans()"
            />
          </b-badge>

          <b-badge
            v-if="statusFilter !== null"
            variant="light-warning"
            class="filter-badge mr-2 mb-1"
          >
            <feather-icon
              icon="UserIcon"
              size="12"
              class="mr-1"
            />
            {{ getStatusText(statusFilter) }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-1 cursor-pointer"
              @click="statusFilter = null; getDeliveryMans()"
            />
          </b-badge>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="clear-filters-btn ml-2 mb-1"
            @click="resetFilters"
          >
            <feather-icon
              icon="XIcon"
              size="12"
            />
            <span class="ml-1">Tout effacer</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Enhanced Loading state -->
    <div
      v-if="isLoading"
      class="loading-state text-center py-5"
    >
      <div class="loading-spinner mb-3">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
        />
      </div>
      <h5 class="text-muted mb-2">
        Chargement en cours...
      </h5>
      <p class="text-muted mb-0">
        Récupération des données des livreurs
      </p>
    </div>

    <!-- Enhanced Empty state -->
    <b-card
      v-else-if="!sortedDeliveryMans.length"
      class="empty-state-card text-center py-5"
    >
      <div class="empty-state">
        <div class="empty-state-icon mb-4">
          <div class="icon-wrapper">
            <feather-icon
              :icon="hasActiveFilters ? 'SearchIcon' : 'UsersIcon'"
              size="64"
              class="text-muted"
            />
          </div>
        </div>

        <h4 class="empty-title mb-3">
          {{ hasActiveFilters ? 'Aucun résultat trouvé' : 'Aucun livreur enregistré' }}
        </h4>

        <p class="empty-description text-muted mb-4">
          <span v-if="hasActiveFilters">
            Aucun livreur ne correspond à vos critères de recherche.
            <br>
            Essayez de modifier vos filtres ou d'effacer la recherche pour voir plus de résultats.
          </span>
          <span v-else>
            Il n'y a pas encore de livreurs enregistrés dans le système.
            <br>
            Commencez par ajouter votre premier livreur pour démarrer la gestion de votre équipe.
          </span>
        </p>

        <div class="empty-actions">
          <b-button
            v-if="hasActiveFilters"
            variant="outline-primary"
            class="mr-2"
            @click="resetFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
            />
            <span class="ml-1">Effacer les filtres</span>
          </b-button>

          <b-button
            v-if="user && user.role === 'admin'"
            :to="{ name: 'delivery-man-create' }"
            variant="primary"
            class="btn-gradient"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            <span class="ml-1">{{ hasActiveFilters ? 'Nouveau livreur' : 'Ajouter le premier livreur' }}</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Enhanced Card view -->
    <div
      v-else-if="viewMode === 'card'"
      class="card-view-container"
    >
      <b-row class="match-height">
        <b-col
          v-for="deliveryMan in sortedDeliveryMans"
          :key="deliveryMan.id"
          lg="4"
          md="6"
          sm="12"
          class="mb-4"
        >
          <b-card class="delivery-man-card enhanced-card h-100">
            <!-- Card Header -->
            <div class="card-header-section d-flex justify-content-between align-items-start">
              <div class="d-flex align-items-center flex-grow-1">
                <div class="user-avatar-wrapper">
                  <b-avatar
                    :text="getInitials(deliveryMan.username)"
                    :variant="deliveryMan.is_active ? 'light-success' : 'light-secondary'"
                    size="xl"
                    class="user-avatar"
                  />
                  <div
                    v-if="deliveryMan.is_active"
                    class="status-indicator active"
                  />
                  <div
                    v-else
                    class="status-indicator inactive"
                  />
                </div>
                <div class="user-info ml-3 flex-grow-1">
                  <h5 class="user-name mb-1">
                    {{ deliveryMan.username }}
                  </h5>
                  <small class="user-email text-muted d-block">
                    {{ deliveryMan.email }}
                  </small>
                  <small class="user-role text-muted">
                    {{ getRoleInFrench(deliveryMan.role) }}
                  </small>
                </div>
              </div>
              <div class="card-status d-flex align-items-center">
                <b-badge
                  v-if="deliveryMan.active_orders_count > 0"
                  variant="light-warning"
                  pill
                  class="status-badge live-delivery-badge d-inline-flex align-items-center font-weight-bold mr-1"
                  v-b-tooltip.hover
                  :title="`${deliveryMan.active_orders_count} colis en cours de livraison par ce livreur`"
                >
                  <span class="live-pulse-dot mr-50" />
                  <span>{{ deliveryMan.active_orders_count }} en livraison</span>
                </b-badge>
                <b-badge
                  :variant="deliveryMan.is_active ? 'primary' : 'secondary'"
                  pill
                  class="status-badge"
                >
                  {{ deliveryMan.is_active ? 'Actif' : 'Inactif' }}
                </b-badge>
              </div>
            </div>

            <!-- Card Content -->
            <div class="card-content mt-3">
              <div class="info-grid">
                <div class="info-item phone-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="text-primary"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      téléphone professionnel
                    </div>            <div class="info-value">
                      <a
                        v-if="deliveryMan.phone"
                        :href="`tel:${deliveryMan.phone}`"
                        class="phone-link"
                      >
                        {{ deliveryMan.phone }}
                      </a>
                      <span
                        v-else
                        class="text-muted"
                      >
                        Non défini
                      </span>
                    </div>
                  </div>
                </div>

                <div class="info-item repository-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="HomeIcon"
                      size="16"
                      class="text-info"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      Dépôt
                    </div>
                    <div class="info-value">
                      <b-badge
                        v-if="deliveryMan.repository_name"
                        variant="light-info"
                        class="repository-badge"
                      >
                        {{ deliveryMan.repository_name }}
                      </b-badge>
                      <span
                        v-else
                        class="text-muted"
                      >
                        Non assigné
                      </span>
                    </div>
                  </div>
                </div>

                <div class="info-item vehicle-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="TruckIcon"
                      size="16"
                      class="text-success"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      Véhicule
                    </div>
                    <div class="info-value">
                      <div
                        v-if="deliveryMan.car_details"
                        class="vehicle-details"
                      >
                        <strong>{{ deliveryMan.car_details.brand }}</strong>
                        <small
                          v-if="deliveryMan.car_details.model"
                          class="d-block text-muted"
                        >
                          {{ deliveryMan.car_details.model }}
                        </small>
                      </div>
                      <span
                        v-else
                        class="text-muted"
                      >
                        Aucun véhicule
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Additional info: Join date -->
                <div class="info-item join-date-info">
                  <div class="info-icon">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="text-secondary"
                    />
                  </div>
                  <div class="info-content">
                    <div class="info-label">
                      Membre depuis
                    </div>
                    <div class="info-value">
                      {{ formatJoinDate(deliveryMan.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="card-footer-section mt-3 pt-3 border-top">
              <div class="d-flex justify-content-between align-items-center">
                <div class="card-stats">
                  <b-badge
                    v-if="getOrdersCount(deliveryMan)"
                    variant="light-warning"
                    class="orders-badge"
                  >
                    <feather-icon
                      icon="PackageIcon"
                      size="12"
                      class="mr-1"
                    />
                    {{ deliveryMan.active_orders_count || 0 }} commande(s)
                  </b-badge>
                  <small
                    v-else
                    class="text-muted"
                  >
                    Aucune commande active
                  </small>
                </div>
                <div class="card-actions">
                  <b-button-group size="sm">
                    <b-button
                      v-b-tooltip.hover
                      title="Voir les détails"
                      variant="outline-info"
                      class="btn-icon action-btn"
                      :to="{ name: 'delivery-man-show', params: { id: deliveryMan.id }}"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="16"
                      />
                    </b-button>
                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      title="Modifier"
                      variant="outline-primary"
                      class="btn-icon action-btn"
                      :to="{ name: 'delivery-man-edit', params: { id: deliveryMan.id }}"
                    >
                      <feather-icon
                        icon="EditIcon"
                        size="16"
                      />
                    </b-button>
                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      title="Changer le mot de passe"
                      variant="outline-warning"
                      class="btn-icon action-btn"
                      @click="openPasswordChangeModal(deliveryMan)"
                    >
                      <feather-icon
                        icon="LockIcon"
                        size="16"
                      />
                    </b-button>
                    <b-button
                      v-if="user && user.role === 'admin'"
                      v-b-tooltip.hover
                      :title="deliveryMan.is_active ? 'Désactiver' : 'Activer'"
                      :variant="deliveryMan.is_active ? 'outline-warning' : 'outline-success'"
                      class="btn-icon action-btn"
                      @click="toggleDeliveryManStatus(deliveryMan)"
                    >
                      <feather-icon
                        :icon="deliveryMan.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                        size="16"
                      />
                    </b-button>
                    <span
                      v-if="user && (user.role === 'admin' || user.role === 'superadmin')"
                      v-b-tooltip.hover
                      :title="deliveryMan.can_be_deleted !== false ? 'Supprimer' : (deliveryMan.deletion_block_reason || 'Impossible de supprimer ce livreur')"
                      class="d-inline-block"
                    >
                      <b-button
                        variant="outline-danger"
                        class="btn-icon action-btn"
                        :disabled="deliveryMan.can_be_deleted === false"
                        :class="{ 'cursor-not-allowed not-allowed': deliveryMan.can_be_deleted === false }"
                        @click="confirmSafeDelete(deliveryMan)"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="16"
                        />
                      </b-button>
                    </span>
                  </b-button-group>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Enhanced Table view -->
    <div
      v-else-if="viewMode === 'table'"
      class="premium-table-wrapper delivery-man-table mb-4"
    >
      <b-card no-body class="mb-0">
        <vue-good-table
          :columns="columns"
          :rows="sortedDeliveryMans"
          :search-options="{ enabled: false }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
            perPageDropdown: [10, 25, 50, 100],
            dropdownAllowAll: false,
          }"
          style-class="vgt-table striped"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- Column: Username / Driver Name -->
            <span v-if="props.column.field === 'username'">
              <div class="d-flex align-items-center">
                <b-avatar
                  :text="getInitials(props.row.username)"
                  :variant="props.row.is_active ? 'light-success' : 'light-secondary'"
                  size="36"
                  class="mr-2 font-weight-bold"
                />
                <div>
                  <div class="d-flex align-items-center">
                    <span class="font-weight-bold text-dark">{{ props.row.username }}</span>
                    <b-badge
                      v-if="props.row.active_orders_count > 0"
                      variant="light-warning"
                      pill
                      class="ml-50 px-50 py-25 font-weight-bold live-delivery-badge d-inline-flex align-items-center"
                      v-b-tooltip.hover
                      :title="`${props.row.active_orders_count} colis en cours de livraison`"
                    >
                      <span class="live-pulse-dot mr-25" />
                      <span class="small font-weight-bold">{{ props.row.active_orders_count }}</span>
                    </b-badge>
                  </div>
                  <small class="text-muted d-block">{{ props.row.email || 'Pas d\'email' }}</small>
                </div>
              </div>
            </span>

            <!-- Column: Phone -->
            <span v-else-if="props.column.field === 'phone'">
              <div
                v-if="props.row.phone"
                class="d-flex align-items-center"
              >
                <feather-icon
                  icon="PhoneIcon"
                  size="14"
                  class="mr-1 text-primary"
                />
                <a
                  :href="`tel:${props.row.phone}`"
                  class="phone-link"
                >{{ props.row.phone }}</a>
              </div>
              <span
                v-else
                class="text-muted"
              >Non défini</span>
            </span>

            <!-- Column: Repository -->
            <span v-else-if="props.column.field === 'repository_name'">
              <b-badge
                v-if="props.row.repository_name"
                variant="light-info"
                pill
              >
                <feather-icon
                  icon="HomeIcon"
                  size="12"
                  class="mr-1"
                />
                {{ props.row.repository_name }}
              </b-badge>
              <span
                v-else
                class="text-muted"
              >Non assigné</span>
            </span>

            <!-- Column: Vehicle -->
            <span v-else-if="props.column.field === 'car_details'">
              <div v-if="props.row.car_details">
                <b-badge
                  variant="light-primary"
                  pill
                >
                  <feather-icon
                    icon="TruckIcon"
                    size="12"
                    class="mr-1"
                  />
                  {{ props.row.car_details.brand }}
                  <span v-if="props.row.car_details.model">({{ props.row.car_details.model }})</span>
                </b-badge>
              </div>
              <span
                v-else
                class="text-muted"
              >Aucun véhicule</span>
            </span>

            <!-- Column: Orders -->
            <span v-else-if="props.column.field === 'active_orders_count'">
              <b-badge
                v-if="getOrdersCount(props.row)"
                variant="light-warning"
                pill
                class="font-weight-bold p-25 px-50 live-delivery-badge d-inline-flex align-items-center"
              >
                <span class="live-pulse-dot mr-50" />
                <span>{{ props.row.active_orders_count }} en livraison</span>
              </b-badge>
              <span
                v-else
                class="text-muted small"
              >
                Aucune commande active
              </span>
            </span>

            <!-- Column: Status -->
            <span v-else-if="props.column.field === 'is_active'">
              <b-badge
                :variant="props.row.is_active ? 'light-success' : 'light-danger'"
                pill
              >
                <feather-icon
                  :icon="props.row.is_active ? 'UserCheckIcon' : 'UserXIcon'"
                  size="12"
                  class="mr-1"
                />
                {{ props.row.is_active ? 'Actif' : 'Inactif' }}
              </b-badge>
            </span>

            <!-- Column: Actions -->
            <span v-else-if="props.column.field === 'action'">
              <b-button-group size="sm">
                <b-button
                  v-b-tooltip.hover
                  title="Voir les détails"
                  variant="outline-info"
                  class="btn-icon"
                  :to="{ name: 'delivery-man-show', params: { id: props.row.id }}"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="14"
                  />
                </b-button>
                <b-button
                  v-if="user && user.role === 'admin'"
                  v-b-tooltip.hover
                  title="Modifier"
                  variant="outline-primary"
                  class="btn-icon"
                  :to="{ name: 'delivery-man-edit', params: { id: props.row.id }}"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                  />
                </b-button>
                <b-button
                  v-if="user && user.role === 'admin'"
                  v-b-tooltip.hover
                  title="Changer le mot de passe"
                  variant="outline-warning"
                  class="btn-icon"
                  @click="openPasswordChangeModal(props.row)"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="14"
                  />
                </b-button>
                <b-button
                  v-if="user && user.role === 'admin'"
                  v-b-tooltip.hover
                  :title="props.row.is_active ? 'Désactiver' : 'Activer'"
                  :variant="props.row.is_active ? 'outline-warning' : 'outline-success'"
                  class="btn-icon"
                  @click="toggleDeliveryManStatus(props.row)"
                >
                  <feather-icon
                    :icon="props.row.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                    size="14"
                  />
                </b-button>
                <span
                  v-if="user && (user.role === 'admin' || user.role === 'superadmin')"
                  v-b-tooltip.hover
                  :title="props.row.can_be_deleted !== false ? 'Supprimer' : (props.row.deletion_block_reason || 'Impossible de supprimer ce livreur')"
                  class="d-inline-block"
                >
                  <b-button
                    variant="outline-danger"
                    class="btn-icon"
                    :disabled="props.row.can_be_deleted === false"
                    :class="{ 'cursor-not-allowed not-allowed': props.row.can_be_deleted === false }"
                    @click="confirmSafeDelete(props.row)"
                  >
                    <feather-icon
                      icon="Trash2Icon"
                      size="14"
                    />
                  </b-button>
                </span>
              </b-button-group>
            </span>

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </b-card>
    </div>

    <!-- Status Toggle Confirmation Modal -->
    <b-modal
      ref="statusModal"
      :title="selectedDeliveryMan && selectedDeliveryMan.is_active ? 'Désactiver le livreur' : 'Activer le livreur'"
      ok-title="Confirmer"
      cancel-title="Annuler"
      ok-variant="primary"
      @ok="handleStatusToggle"
    >
      <p v-if="selectedDeliveryMan">
        Êtes-vous sûr de vouloir {{ selectedDeliveryMan.is_active ? 'désactiver' : 'activer' }}
        le livreur <strong>{{ selectedDeliveryMan.username }}</strong> ?
      </p>
    </b-modal>

    <!-- Password Change Modal -->
    <b-modal
      id="password-change-modal"
      v-model="showPasswordChangeModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordChangeModal"
    >
      <template #modal-header="{ close }">
        <div class="w-100">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div class="password-icon-wrapper-modal mr-2">
                <feather-icon
                  icon="LockIcon"
                  size="20"
                  class="text-primary"
                />
              </div>
              <div>
                <h5 class="mb-0">
                  Changer le mot de passe
                </h5>
                <small
                  v-if="selectedDeliveryManForPassword"
                  class="text-muted"
                >
                  {{ selectedDeliveryManForPassword.username }} • {{ selectedDeliveryManForPassword.email }}
                </small>
              </div>
            </div>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="close"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </div>
        </div>
      </template>

      <div v-if="selectedDeliveryManForPassword">
        <validation-observer
          ref="passwordFormValidator"
          v-slot="{ handleSubmit }"
        >
          <b-form @submit.prevent="handleSubmit(savePasswordChange)">
            <!-- Password Input -->
            <b-row>
              <b-col cols="12">
                <b-form-group
                  label="Nouveau mot de passe *"
                  label-for="modal-password"
                  label-class="form-label-premium"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="modal-password"
                  >
                    <b-input-group class="premium-input-group">
                      <b-form-input
                        id="modal-password"
                        v-model="modalPasswordForm.password"
                        :type="showModalPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : (passwordStrength > 25 ? true : null)"
                        placeholder="Entrez un mot de passe sécurisé"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-secondary"
                          class="password-toggle-btn"
                          @click="showModalPassword = !showModalPassword"
                        >
                          <feather-icon
                            :icon="showModalPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            size="16"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <!-- Password Strength Indicator -->
                    <div
                      v-if="modalPasswordForm.password"
                      class="password-strength-wrapper mt-2"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <small class="strength-label">Force du mot de passe :</small>
                        <small
                          class="strength-text"
                          :class="passwordStrengthClass"
                        >{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="6px"
                        class="premium-progress"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password Input -->
              <b-col cols="12">
                <b-form-group
                  label="Confirmer le mot de passe *"
                  label-for="modal-confirm-password"
                  label-class="form-label-premium"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:modal-password"
                  >
                    <b-input-group class="premium-input-group">
                      <b-form-input
                        id="modal-confirm-password"
                        v-model="modalPasswordForm.confirm_password"
                        :type="showModalConfirmPassword ? 'text' : 'password'"
                        :state="getConfirmPasswordState(errors)"
                        placeholder="Confirmez le mot de passe"
                        class="premium-input"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-secondary"
                          class="password-toggle-btn"
                          @click="showModalConfirmPassword = !showModalConfirmPassword"
                        >
                          <feather-icon
                            :icon="showModalConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            size="16"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>

                    <!-- Real-time validation feedback -->
                    <div
                      v-if="modalPasswordForm.confirm_password"
                      class="mt-1"
                    >
                      <small
                        v-if="passwordsMatch && modalPasswordForm.password"
                        class="text-success d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="CheckCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe correspondent
                      </small>
                      <small
                        v-else-if="modalPasswordForm.password"
                        class="text-danger d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="XCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe ne correspondent pas
                      </small>
                      <small
                        v-else
                        class="text-warning d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="AlertTriangleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Veuillez d'abord saisir un mot de passe
                      </small>
                    </div>
                    <small
                      v-if="errors.length > 0 && !modalPasswordForm.confirm_password"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Generate Password Button -->
            <div class="text-center mb-3 mt-2">
              <PasswordGenerator
                :user="selectedDeliveryManForPassword"
                user-type="deliveryman"
                @generated="onPasswordGenerated"
              />
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                class="premium-btn-secondary"
                @click="closePasswordChangeModal"
              >
                <feather-icon
                  icon="XIcon"
                  size="16"
                  class="mr-50"
                />
                Annuler
              </b-button>
              <span id="password-save-btn-wrapper" class="d-inline-block">
                <b-button
                  type="submit"
                  variant="primary"
                  class="premium-btn-primary save-btn-animated"
                  :class="{ 'btn-saving': isSavingPassword, 'not-allowed': !canSavePassword || isSavingPassword }"
                  :disabled="!canSavePassword || isSavingPassword"
                >
                  <b-spinner
                    v-if="isSavingPassword"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    size="16"
                    class="mr-50"
                  />
                  Enregistrer
                </b-button>
                <b-tooltip
                  v-if="!canSavePassword || isSavingPassword"
                  target="password-save-btn-wrapper"
                  triggers="hover focus"
                  placement="top"
                >
                  <template v-if="isSavingPassword">
                    Enregistrement en cours...
                  </template>
                  <template v-else-if="modalPasswordForm.password && modalPasswordForm.confirm_password && !passwordsMatch">
                    Les mots de passe ne correspondent pas.
                  </template>
                  <template v-else-if="modalPasswordForm.password && modalPasswordForm.password.length < 8">
                    Le mot de passe doit comporter au moins 8 caractères.
                  </template>
                  <template v-else-if="!modalPasswordForm.password || !modalPasswordForm.confirm_password">
                    Veuillez remplir les champs mot de passe et confirmation.
                  </template>
                  <template v-else>
                    Veuillez remplir correctement les champs.
                  </template>
                </b-tooltip>
              </span>
            </div>
          </b-form>
        </validation-observer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar,
  BButton,
  BSpinner,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCard,
  BBadge,
  BRow,
  BCol,
  BButtonGroup,
  BModal,
  BForm,
  BProgress,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { VueGoodTable } from 'vue-good-table'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import storeAuth from '@/store/store'

// Register validation rules
extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  name: 'ListDrivers',
  components: {
    PasswordGenerator,
    BAvatar,
    BButton,
    BSpinner,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCard,
    BBadge,
    BRow,
    BCol,
    BButtonGroup,
    BModal,
    BForm,
    BProgress,
    BTooltip,
    vSelect,
    VueGoodTable,
    ValidationProvider,
    ValidationObserver,
  },

  data() {
    return {
      isLoading: false,
      pageLength: 100,
      dir: false,
      delivery_mans: [],
      searchTerm: '',
      user: {},
      repositoryFilter: null,
      statusFilter: null,
      repositories: [],
      selectedDeliveryMan: null,
      viewMode: 'card', // 'card' or 'table'
      columns: [
        { label: 'Livreur', field: 'username' },
        { label: 'Téléphone', field: 'phone' },
        { label: 'Dépôt', field: 'repository_name' },
        { label: 'Véhicule', field: 'car_details' },
        { label: 'Commandes', field: 'active_orders_count' },
        { label: 'Statut', field: 'is_active' },
        { label: 'Actions', field: 'action', sortable: false },
      ],
      deliveryManStats: {
        total: 0,
        active: 0,
        inactive: 0,
        withCars: 0,
      },
      tableKey: 0,
      searchTimer: null,
      statusOptions: [
        { value: true, text: 'Actif', variant: 'success' },
        { value: false, text: 'Inactif', variant: 'danger' },
      ],
      // Password change modal state
      showPasswordChangeModal: false,
      selectedDeliveryManForPassword: null,
      modalPasswordForm: {
        password: '',
        confirm_password: '',
      },
      showModalPassword: false,
      showModalConfirmPassword: false,
      isSavingPassword: false,
    }
  },

  watch: {
    '$route.query': {
      immediate: true,
      handler(newQuery) {
        const view = newQuery.view || 'card'
        const search = newQuery.search || ''
        const repo = newQuery.repository ? Number(newQuery.repository) : null
        let status = null
        if (newQuery.is_active === 'true') status = true
        if (newQuery.is_active === 'false') status = false

        const stateChanged = this.viewMode !== view || this.searchTerm !== search || this.repositoryFilter !== repo || this.statusFilter !== status

        this.viewMode = view
        this.searchTerm = search
        this.repositoryFilter = repo
        this.statusFilter = status

        if (stateChanged || !this.delivery_mans.length) {
          this.getDeliveryMans()
        }
      },
    },
  },

  computed: {
    hasActiveFilters() {
      return this.searchTerm || this.repositoryFilter || this.statusFilter !== null
    },

    sortedDeliveryMans() {
      let filtered = [...this.delivery_mans]

      // Apply status filter if set
      if (this.statusFilter !== null) {
        filtered = filtered.filter(dm => dm.is_active === this.statusFilter)
      }

      // If no filters are applied, sort by active status first (active first, then inactive)
      if (!this.hasActiveFilters) {
        return filtered.sort((a, b) => {
          // Active users first (true comes before false)
          if (a.is_active !== b.is_active) {
            return b.is_active - a.is_active
          }
          // If same status, sort by creation date descending (newest first)
          return new Date(b.created_at) - new Date(a.created_at)
        })
      }
      // If filters are applied, return filtered results
      return filtered
    },

    passwordStrength() {
      let strength = 0
      const { password } = this.modalPasswordForm

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special chars

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.modalPasswordForm.password === this.modalPasswordForm.confirm_password
      )
    },

    canSavePassword() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.passwordsMatch
        && this.modalPasswordForm.password.length >= 8
      )
    },
  },

  created() {
    this.user = storeAuth.state.user
    if (this.user && this.user.role === 'admin') {
      this.fetchRepositories()
    }
  },

  methods: {
    updateUrlQuery() {
      const query = {}
      if (this.viewMode && this.viewMode !== 'card') {
        query.view = this.viewMode
      }
      if (this.searchTerm) {
        query.search = this.searchTerm
      }
      if (this.repositoryFilter) {
        query.repository = String(this.repositoryFilter)
      }
      if (this.statusFilter !== null) {
        query.is_active = String(this.statusFilter)
      }

      const currentQuery = this.$route.query || {}
      if (JSON.stringify(query) !== JSON.stringify(currentQuery)) {
        this.$router.replace({ query }).catch(() => {})
      }
    },

    async getDeliveryMans() {
      this.updateUrlQuery()
      this.isLoading = true
      try {
        // Use the new dedicated endpoint instead of filtering
        const params = {}
        if (this.repositoryFilter) {
          params.repository = this.repositoryFilter
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }
        if (this.statusFilter !== null) {
          params.is_active = this.statusFilter
        }

        const { data } = await axios.get('/api/auth/users/delivery-men/', { params })
        this.delivery_mans = data
        this.calculateStats()
        this.tableKey += 1 // Force table re-render
      } catch (error) {
        this.showToast('danger', 'top-center', 'Error loading delivery men')
      } finally {
        this.isLoading = false
      }
    },

    refreshData() {
      this.getDeliveryMans()
    },

    getPercentage(value, total) {
      if (total === 0) return 0
      return Math.round((value / total) * 100)
    },

    getRoleInFrench(role) {
      const roleMappings = {
        delivery_man: 'Livreur',
        admin: 'Administrateur',
        manager: 'Manager',
        user: 'Utilisateur',
      }
      return roleMappings[role] || role
    },

    getStatusText(statusValue) {
      return statusValue ? 'Actif' : 'Inactif'
    },
    deleteDeliveryMen(id) {
      // This method is deprecated - use toggleDeliveryManStatus instead
      this.toggleDeliveryManStatus({ id })
    },

    async toggleDeliveryManStatus(deliveryMan) {
      try {
        const result = await this.$swal({
          title: deliveryMan.is_active ? 'Désactiver le livreur' : 'Activer le livreur',
          text: `Êtes-vous sûr de vouloir ${deliveryMan.is_active ? 'désactiver' : 'activer'} ce livreur?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: deliveryMan.is_active ? 'Oui, désactiver' : 'Oui, activer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          this.isLoading = true
          try {
            const response = await axios.post(`/api/auth/users/delivery-men/toggle-activation/${deliveryMan.id}/`)
            this.showToast(
              'success',
              'top-center',
              `Livreur ${response.data.is_active ? 'activé' : 'désactivé'} avec succès`,
            )
            await this.getDeliveryMans()
          } catch (error) {
            this.showToast('danger', 'top-center', error.response?.data?.error || 'Erreur lors du changement de statut')
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed, do nothing
      }
    },

    calculateStats() {
      this.deliveryManStats = {
        total: this.delivery_mans.length,
        active: this.delivery_mans.filter(dm => dm.is_active).length,
        inactive: this.delivery_mans.filter(dm => !dm.is_active).length,
        withCars: this.delivery_mans.filter(dm => dm.car_details).length,
      }
    },

    // toast
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    getOrdersCount(deliveryMan) {
      // Use actual order count if available from API
      return deliveryMan.active_orders_count || 0
    },

    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des dépôts')
      }
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'card' : 'table'
      this.updateUrlQuery()
    },

    onSearchChange() {
      // Debounced search
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.updateUrlQuery()
        this.getDeliveryMans()
      }, 500)
    },

    resetFilters() {
      this.searchTerm = ''
      this.repositoryFilter = null
      this.statusFilter = null
      this.updateUrlQuery()
      this.getDeliveryMans()
    },

    confirmToggleStatus(deliveryMan) {
      this.selectedDeliveryMan = deliveryMan
      this.$refs.statusModal.show()
    },

    async handleStatusToggle() {
      if (!this.selectedDeliveryMan) return

      try {
        this.isLoading = true
        const response = await axios.post(`/api/auth/users/delivery-men/toggle-activation/${this.selectedDeliveryMan.id}/`)

        this.showToast(
          'success',
          'top-center',
          `Livreur ${response.data.is_active ? 'activé' : 'désactivé'} avec succès`,
        )

        // Refresh the entire list to ensure data consistency and table reactivity
        await this.getDeliveryMans()
      } catch (error) {
        this.showToast('danger', 'top-center', error.response?.data?.error || 'Erreur lors du changement de statut')
      } finally {
        this.isLoading = false
      }
    },

    exportToCSV() {
      try {
        // Format data for CSV
        const csvData = this.delivery_mans.map(driver => ({
          Nom: driver.username || '',
          Email: driver.email || '',
          'téléphone professionnel': driver.phone || '',
          Dépôt: driver.repository_name || '',
          Véhicule: driver.car_details ? `${driver.car_details.brand} ${driver.car_details.model}` : '',
          Statut: driver.is_active ? 'Actif' : 'Inactif',
        }))

        // Convert to CSV string
        const headers = Object.keys(csvData[0]).join(',')
        const rows = csvData.map(obj => Object.values(obj).map(val => `"${val}"`).join(','))
        const csvContent = [headers, ...rows].join('\n')

        // Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `livreurs_${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'top-right', 'Données exportées avec succès')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error exporting to CSV:', error)
        this.showToast('danger', 'top-right', "Erreur lors de l'exportation")
      }
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    getRepositoryName(repositoryId) {
      const repo = this.repositories.find(r => r.id === repositoryId)
      return repo ? repo.name : 'Inconnu'
    },

    formatJoinDate(dateString) {
      if (!dateString) return 'Non défini'
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays < 30) {
        return `${diffDays} jour(s)`
      } if (diffDays < 365) {
        const months = Math.floor(diffDays / 30)
        return `${months} mois`
      }
      const years = Math.floor(diffDays / 365)
      return `${years} an(s)`
    },

    // Password Change Modal Methods
    openPasswordChangeModal(deliveryMan) {
      this.selectedDeliveryManForPassword = deliveryMan
      this.modalPasswordForm = {
        password: '',
        confirm_password: '',
      }
      this.showModalPassword = false
      this.showModalConfirmPassword = false
      this.showPasswordChangeModal = true
    },

    closePasswordChangeModal() {
      this.showPasswordChangeModal = false
    },

    resetPasswordChangeModal() {
      this.selectedDeliveryManForPassword = null
      this.modalPasswordForm = {
        password: '',
        confirm_password: '',
      }
      this.showModalPassword = false
      this.showModalConfirmPassword = false
      this.isSavingPassword = false

      // Reset validation
      if (this.$refs.passwordFormValidator) {
        this.$refs.passwordFormValidator.reset()
      }
    },

    onPasswordGenerated(generatedPassword) {
      this.modalPasswordForm.password = generatedPassword
      this.modalPasswordForm.confirm_password = generatedPassword
      this.showModalPassword = true
      this.showModalConfirmPassword = true
      this.$forceUpdate()
    },

    onPasswordInput() {
      // Trigger real-time validation
      this.$forceUpdate()
    },

    getConfirmPasswordState(errors) {
      if (errors.length > 0) return false

      // Show validation state only if user has typed in confirm field
      if (!this.modalPasswordForm.confirm_password) return null

      // Real-time match validation
      return !!this.passwordsMatch
    },

    async savePasswordChange() {
      if (!this.canSavePassword || !this.selectedDeliveryManForPassword) return

      this.isSavingPassword = true
      try {
        await axios.post(
          `/api/auth/users/delivery-men/change-password/${this.selectedDeliveryManForPassword.id}/`,
          this.modalPasswordForm,
        )

        this.showToast('success', 'top-right', 'Mot de passe modifié avec succès')
        this.closePasswordChangeModal()
      } catch (error) {
        const errorMsg = error.response?.data?.error
          || error.response?.data?.detail
          || 'Erreur lors du changement de mot de passe'
        this.showToast('danger', 'top-right', errorMsg)
      } finally {
        this.isSavingPassword = false
      }
    },

    async confirmSafeDelete(deliveryMan) {
      try {
        const result = await this.$swal({
          title: 'Supprimer le livreur ?',
          text: `Êtes-vous sûr de vouloir supprimer ${deliveryMan.username || 'ce livreur'} ?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, supprimer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })

        if (result.value) {
          this.isLoading = true
          try {
            await axios.delete(`/api/auth/users/delivery-men/${deliveryMan.id}/`)
            await this.getDeliveryMans()
            this.showToast('success', 'top-center', `Le livreur ${deliveryMan.username} a été supprimé avec succès`)
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message
            this.showToast('danger', 'top-center', errorMsg)
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/user-modules.scss";

// Custom styles specific to this view
.delivery-man-card {
  @extend .user-card;
}

.delivery-man-table {
  ::v-deep .vgt-table {
    @extend .enhanced-table;
  }
}

// Apply v-select-enhanced to all v-selects in this component
::v-deep .v-select {
  @extend .v-select-enhanced;
}

/* Enhanced repository selector styling */
.repository-selector {
  /* Apply enhanced styling from user-modules.scss */
  @extend .v-select-enhanced;

  /* Additional styling specific to repository selector */
  .vs__dropdown-toggle {
    border: 1px solid #d8d6de;
    transition: all 0.3s;

    /* Add subtle box-shadow for better visibility */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  }

  .vs__dropdown-option {
    padding: 10px 20px;
    border-bottom: 1px solid #f3f2f7;

    &:last-child {
      border-bottom: none;
    }
  }

  &.vs--open {
    .vs__dropdown-toggle {
      box-shadow: 0 3px 10px rgba(115, 103, 240, 0.1);
    }
  }

  .vs__selected {
    padding: 2px 8px;
    margin: 0;
  }
}

/* Enhanced filtering dropdowns */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 4px 8px;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem;
    background-color: #f8f8f8;
    transition: all 0.3s ease-in-out;
    min-height: 38px;

    &:hover {
      border-color: #0d4399 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #0d4399 !important;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.1);
  }

  .vs__selected {
    margin: 0 2px;
    padding: 0 5px;
    color: #5e5873;
  }

  .vs__search::placeholder {
    color: #b9b9c3;
  }

  .vs__dropdown-menu {
    padding: 0;
    border: 1px solid #d8d6de;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
}

.border-select .vs__dropdown-toggle {
  border: 1px solid #d8d6de !important;
}

.cursor-not-allowed,
.not-allowed {
  cursor: not-allowed !important;
  pointer-events: auto !important;
  opacity: 0.65;
}

.active-delivery-indicator {
  animation: pulse-delivery 2s infinite;
  box-shadow: 0 0 8px rgba(255, 159, 67, 0.4);
}

.live-pulse-dot {
  width: 8px;
  height: 8px;
  background-color: #ff9f43;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  box-shadow: 0 0 0 0 rgba(255, 159, 67, 0.7);
  animation: live-dot-pulse 1.5s infinite cubic-bezier(0.66, 0, 0, 1);
}

@keyframes live-dot-pulse {
  to {
    box-shadow: 0 0 0 8px rgba(255, 159, 67, 0);
  }
}

@keyframes pulse-delivery {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 159, 67, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(255, 159, 67, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 159, 67, 0);
  }
}

/* Empty state styling */
.empty-state {
  padding: 2rem;

  .empty-state-icon {
    opacity: 0.6;
  }
}

/* Cursor pointer for interactive elements */
.cursor-pointer {
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

/* Add styles for user details */
.user-detail {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.kpi-card {
  border: 0;
  border-left: 4px solid transparent;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  .card-body {
    padding: 1rem 1.1rem;
  }

  .kpi-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .kpi-label {
    font-size: 0.8rem;
    color: #6e6b7b;
  }

  .kpi-value {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.1;
    margin-top: 0.25rem;
  }

  .kpi-subtitle {
    font-size: 0.75rem;
    color: #b9b9c3;
    margin-top: 0.25rem;
  }

  .kpi-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.kpi-neutral {
    background: rgba(108, 117, 125, 0.06);
    border-left-color: #6c757d;
  }

  &.kpi-success {
    background: rgba(40, 199, 111, 0.06);
    border-left-color: #28c76f;
  }

  &.kpi-danger {
    background: rgba(17, 64, 146, 0.06);
    border-left-color: #114092;
  }

  &.kpi-info {
    background: rgba(0, 207, 232, 0.06);
    border-left-color: #00cfe8;
  }

  .kpi-icon-neutral {
    background: rgba(108, 117, 125, 0.14);
  }

  .kpi-icon-success {
    background: rgba(40, 199, 111, 0.14);
  }

  .kpi-icon-danger {
    background: rgba(234, 84, 85, 0.14);
  }

  .kpi-icon-info {
    background: rgba(0, 207, 232, 0.14);
  }
}

/* Statistics cards styling */
.stats-card {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.1);
  }

  .avatar {
    height: 3.5rem;
    width: 3.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.357rem;
  }
}

/* Fix for table actions layout */
.vgt-table td .d-flex {
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
}

.vgt-table td .btn-icon {
  margin-right: 0.25rem;
}

/* Enhanced Card View Styling */
.card-view-container {
  .enhanced-card {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #0A2634, #ff4d4d);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(10, 38, 52, 0.15);
      border-color: #0A2634;

      &::before {
        opacity: 1;
      }

      .user-avatar {
        transform: scale(1.05);
      }

      .action-btn {
        transform: translateY(-2px);
      }
    }

    .card-header-section {
      padding: 1.5rem 1.5rem 1rem;

      .user-avatar-wrapper {
        position: relative;

        .user-avatar {
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .status-indicator {
          position: absolute;
          bottom: 2px;
          right: 2px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;

          &.active {
            background-color: #28a745;
            box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.2);
          }

          &.inactive {
            background-color: #dc3545;
            box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.2);
          }
        }
      }

      .user-info {
        .user-name {
          font-weight: 600;
          color: #2c2c54;
          margin-bottom: 0.25rem;
        }

        .user-email {
          font-size: 0.875rem;
          color: #6c757d;
          margin-bottom: 0.25rem;
        }

        .user-role {
          font-size: 0.75rem;
          color: #0A2634;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .status-badge {
        font-weight: 600;
        padding: 0.5rem 1rem;
        font-size: 0.75rem;
        letter-spacing: 0.5px;
      }
    }

    .card-content {
      padding: 0 1.5rem;

      .info-grid {
        display: grid;
        gap: 1rem;
        grid-template-columns: repeat(2, 1fr);

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }

        .info-item {
          display: flex;
          align-items: center;
          padding: 0.75rem;
          background: #f8f9fa;
          border-radius: 0.375rem;
          transition: all 0.3s ease;

          &:hover {
            background: #e9ecef;
            transform: translateX(5px);
          }

          &.phone-info:hover {
            background: linear-gradient(45deg, #e3f2fd, #f3e5f5);
          }

          &.repository-info:hover {
            background: linear-gradient(45deg, #e8f5e8, #f0f8ff);
          }

          &.vehicle-info:hover {
            background: linear-gradient(45deg, #f0fff0, #f5f5dc);
          }

          &.join-date-info:hover {
            background: linear-gradient(45deg, #fafafa, #f0f0f0);
          }

          .info-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            background: white;
            border-radius: 0.375rem;
            margin-right: 0.75rem;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .info-content {
            flex: 1;

            .info-label {
              font-size: 0.75rem;
              font-weight: 600;
              color: #6c757d;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 0.25rem;
            }

            .info-value {
              font-size: 0.875rem;
              font-weight: 500;
              color: #495057;

              .phone-link {
                color: #0A2634;
                text-decoration: none;
                transition: all 0.3s ease;

                &:hover {
                  color: #cc0000;
                  text-decoration: underline;
                }
              }

              .repository-badge {
                font-weight: 600;
                padding: 0.25rem 0.5rem;
                border-radius: 0.25rem;
                background: linear-gradient(45deg, rgba(10, 38, 52, 0.1), rgba(10, 38, 52, 0.2));
                color: #0A2634;
                border: 1px solid rgba(10, 38, 52, 0.2);
              }

              .vehicle-details {
                strong {
                  color: #28a745;
                }

                small {
                  font-size: 0.75rem;
                  margin-top: 0.125rem;
                }
              }
            }
          }
        }
      }
    }

    .card-footer-section {
      padding: 1rem 1.5rem 1.5rem;
      background: rgba(115, 103, 240, 0.02);

      .orders-badge {
        background: linear-gradient(45deg, #ffc107, #ffeb3b);
        color: #495057;
        font-weight: 600;
        border: none;
        padding: 0.5rem 0.75rem;
      }

      .action-btn {
        transition: all 0.3s ease;
        border-radius: 0.375rem !important;
        margin-left: 0.25rem;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      }
    }
  }
}

/* Enhanced Page Header */
.page-header {
  .page-title {
    font-weight: 700;
    color: #2c2c54;
  }

  .btn-gradient {
    background: linear-gradient(45deg, #0A2634, #ff4d4d);
    border: none;
    box-shadow: 0 4px 15px rgba(10, 38, 52, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(10, 38, 52, 0.4);
      background: linear-gradient(45deg, #cc0000, #0A2634);
    }
  }

  .rotate-animation {
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}

/* Enhanced Filter Card */
.filter-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

  .filter-header h5 {
    color: #2c2c54;
    font-weight: 600;
  }

  .enhanced-input {
    border: 1px solid #e9ecef;
    transition: all 0.3s ease;

    &:focus {
      border-color: #0A2634;
      box-shadow: 0 3px 10px rgba(10, 38, 52, 0.1);
    }
  }

  .search-btn {
    background: linear-gradient(45deg, #0A2634, #ff4d4d);
    border: none;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      background: linear-gradient(45deg, #cc0000, #0A2634);
    }
  }

  .active-filters {
    .filter-badge {
      background: linear-gradient(45deg, #ffe6e6, #ffcccc);
      color: #0A2634;
      border: 1px solid #ffcccc;
      padding: 0.5rem 0.75rem;
      border-radius: 1rem;
      font-weight: 500;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(10, 38, 52, 0.2);
        background: linear-gradient(45deg, #ffdddd, #ffb3b3);
      }

      .cursor-pointer {
        margin-left: 0.5rem;
        padding: 0.25rem;
        border-radius: 50%;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(10, 38, 52, 0.1);
        }
      }
    }

    .clear-filters-btn {
      border-radius: 1rem;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }
  }
}

/* Enhanced Statistics Cards */
.stats-card {
  border-radius: 0.5rem;
  border: none;
  overflow: hidden;
  position: relative;

  &.stats-primary {
    background: linear-gradient(135deg, #0A2634 0%, #ff4d4d 100%);
    color: white;

    .text-primary {
      color: white !important;
    }
  }

  &.stats-success {
    background: linear-gradient(135deg, #28c76f 0%, #20c997 100%);
    color: white;

    .text-success {
      color: white !important;
    }
  }

  &.stats-warning {
    background: linear-gradient(135deg, #0d4399 0%, #fd7e14 100%);
    color: white;

    .text-warning {
      color: white !important;
    }
  }

  &.stats-info {
    background: linear-gradient(135deg, #00cfe8 0%, #1D1D1B 100%);
    color: white;

    .text-info {
      color: white !important;
    }
  }

  .stats-progress {
    .progress {
      height: 4px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;

      .progress-bar {
        background: rgba(255, 255, 255, 0.8);
        border-radius: 2px;
      }
    }
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
}

/* Enhanced Empty State */
.empty-state-card {
  border: 2px dashed #e9ecef;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);

  .empty-state {
    .icon-wrapper {
      display: inline-block;
      padding: 2rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-radius: 50%;
      margin-bottom: 1rem;
    }

    .empty-title {
      color: #2c2c54;
      font-weight: 600;
    }

    .empty-description {
      font-size: 1rem;
      line-height: 1.6;
    }

    .empty-actions .btn-gradient {
      background: linear-gradient(45deg, #0A2634, #ff4d4d);
      border: none;
      box-shadow: 0 4px 15px rgba(10, 38, 52, 0.3);
    }
  }
}

/* Loading State Enhancement */
.loading-state {
  .loading-spinner {
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
}

/* Responsive Enhancements */
@media (max-width: 1200px) {
  .card-view-container .enhanced-card {
    .card-header-section {
      padding: 1.25rem 1.25rem 0.75rem;
    }

    .card-content {
      padding: 0 1.25rem;
    }

    .card-footer-section {
      padding: 0.75rem 1.25rem 1.25rem;
    }
  }
}

@media (max-width: 768px) {
  .page-header {
    .d-flex {
      flex-direction: column;
      align-items: flex-start !important;

      .d-flex {
        margin-top: 1rem;
        width: 100%;
        justify-content: flex-end;
      }
    }
  }

  .card-view-container .enhanced-card {
    .card-header-section {
      .d-flex {
        flex-direction: column;
        align-items: flex-start !important;

        .user-avatar-wrapper {
          margin-bottom: 1rem;
        }

        .card-status {
          align-self: flex-end;
          margin-top: -3rem;
        }
      }
    }

    .card-footer-section {
      .d-flex {
        flex-direction: column;
        align-items: flex-start !important;

        .card-actions {
          margin-top: 0.75rem;
          width: 100%;

          .b-button-group {
            width: 100%;

            .action-btn {
              flex: 1;
            }
          }
        }
      }
    }
  }

  .filter-card {
    .d-flex {
      flex-direction: column;
      align-items: flex-start !important;

      .d-flex {
        margin-top: 0.75rem;
        width: 100%;
      }
    }
  }
}

@media (max-width: 576px) {
  .stats-card {
    margin-bottom: 1rem !important;

    .d-flex {
      .avatar {
        display: none;
      }
    }
  }

  .card-view-container {
    .enhanced-card {
      margin-bottom: 1.5rem;

      .info-grid {
        gap: 0.75rem;
      }

      .card-actions .b-button-group {
        .action-btn {
          padding: 0.5rem;

          span {
            display: none;
          }
        }
      }
    }
  }
}

/* Animation Enhancements */
.card-view-container .enhanced-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-card {
  animation: fadeInDown 0.6s ease-out;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-card {
  animation: fadeInScale 0.6s ease-out;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Dark mode support (if needed) */
@media (prefers-color-scheme: dark) {
  .enhanced-card {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%) !important;
    color: #ecf0f1 !important;
    border-color: #34495e !important;

    .info-item {
      background: #34495e !important;
      color: #ecf0f1 !important;

      &:hover {
        background: #3c5470 !important;
      }
    }

    .user-name,
    .info-value {
      color: #ecf0f1 !important;
    }

    .info-label {
      color: #bdc3c7 !important;
    }
  }

  .filter-card {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%) !important;
    color: #ecf0f1 !important;
    border-color: #34495e !important;
  }
}

/* Password Change Modal Premium Styling */
#password-change-modal {
  ::v-deep .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  ::v-deep .modal-header {
    border-bottom: 1px solid #e2e8f0;
    padding: 1.5rem;
  }

  ::v-deep .modal-body {
    padding: 1.5rem;
  }

  .password-icon-wrapper-modal {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);
  }

  .form-label-premium {
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .generate-password-btn-centered {
    padding: 0.75rem 2rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.9375rem;
    border: 2px solid #0d4399;
    color: #0d4399;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);

    &:hover {
      background: linear-gradient(135deg, #0d4399 0%, #0a3370 100%);
      color: #ffffff;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
      border-color: #0d4399;
    }

    &:active {
      transform: translateY(0);
    }
  }

  .premium-input-group {
    .form-control {
      border: 1px solid #e2e8f0;
      padding: 0.75rem 1rem;
      border-radius: 8px 0 0 8px;
      font-size: 0.9375rem;
      transition: all 0.2s ease;

      &:focus {
        border-color: #0d4399;
        box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.1);
      }

      &.is-valid {
        border-color: #28a745;

        &:focus {
          box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.15);
        }
      }

      &.is-invalid {
        border-color: #dc3545;

        &:focus {
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
        }
      }
    }

    .input-group-append {
      .password-toggle-btn {
        border: 1px solid #e2e8f0;
        border-left: none;
        border-radius: 0 8px 8px 0;
        background: #f8fafc;
        color: #64748b;
        padding: 0.75rem 1rem;
        transition: all 0.2s ease;

        &:hover {
          background: #0d4399;
          color: #ffffff;
          border-color: #0d4399;
        }
      }
    }
  }

  .password-strength-wrapper {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.75rem;

    .strength-label {
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      font-size: 0.7rem;
    }

    .strength-text {
      font-weight: 700;
      font-size: 0.75rem;

      &.text-danger {
        color: #dc3545 !important;
      }

      &.text-warning {
        color: #ffc107 !important;
      }

      &.text-info {
        color: #17a2b8 !important;
      }

      &.text-success {
        color: #28a745 !important;
      }
    }

    .premium-progress {
      border-radius: 10px;
      background: #e2e8f0;
      overflow: hidden;

      ::v-deep .progress-bar {
        border-radius: 10px;
        transition: width 0.3s ease;
      }
    }
  }

  .premium-btn-secondary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    border: 1px solid #e2e8f0;
    color: #64748b;
    background: #ffffff;
    transition: all 0.2s ease;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      color: #475569;
      transform: translateY(-1px);
    }
  }

  .premium-btn-primary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    background: linear-gradient(135deg, #0d4399 0%, #0a3370 100%);
    border: none;
    color: #ffffff;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &.not-allowed,
    &:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      pointer-events: auto !important;
    }

    &.btn-saving {
      animation: pulse-save 1.5s ease-in-out infinite;
    }
  }
}

@keyframes pulse-save {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(13, 67, 153, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(13, 67, 153, 0);
  }
}

/* Validation message animation */
.validation-message-animate {
  animation: fadeInSlide 0.3s ease-out;
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
